// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { orgDefaultCurrencyCodeSchema } from '@/constants/currency';
import { currencyOptionLabel } from '@/currency/utilities/format';
import { MenuItem, TextField, TextFieldProps, Tooltip } from '@mui/material';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';

interface Props<Values extends FieldValues> {
  /**
   * Path of the ISO-code field the selector binds.
   */
  name: FieldPath<Values>;
  control: Control<Values>;
  label?: string;
  disabled?: boolean;
  /**
   * Tooltip shown on hover over the disabled selector, explaining why the
   * currency cannot be changed. Only rendered when disabled.
   */
  disabledTooltip?: string;
  fullWidth?: boolean;
  sx?: TextFieldProps['sx'];
}

/**
 * A form-bound currency selector offering the app-allowed currencies, matching
 * the organization default-currency picker. Binds a single ISO-code field.
 */
export function FormCurrencySelect<Values extends FieldValues>(
  props: Props<Values>
) {
  return (
    <Controller
      control={props.control}
      name={props.name}
      render={({ field }) => {
        const select = (
          <TextField
            {...field}
            select
            label={props.label}
            size="small"
            fullWidth={props.fullWidth}
            disabled={props.disabled}
            sx={props.sx}
          >
            {orgDefaultCurrencyCodeSchema.options.map((currencyCode) => (
              <MenuItem key={currencyCode} value={currencyCode}>
                {currencyOptionLabel(currencyCode)}
              </MenuItem>
            ))}
          </TextField>
        );

        // A disabled control swallows hover events, so wrap it in a span for the
        // tooltip to anchor to.
        if (props.disabled === true && props.disabledTooltip !== undefined) {
          return (
            <Tooltip title={props.disabledTooltip} placement="top" arrow>
              <span>{select}</span>
            </Tooltip>
          );
        }

        return select;
      }}
    />
  );
}
